import { Component, OnInit } from '@angular/core';
import { AiAssistService } from '../ai-assist.service';

interface Tile {
  key: string;
  title: string;
  chart: string;
  loading: boolean;
  error: boolean;
  labels: string[];
  data: number[];
  value: number | null;
  unit: string;
  real: boolean;
  datasets: { data: number[]; label: string }[];
}

/**
 * Flexible, editable Store Intelligence cockpit.
 * Tiles are config-driven (from /store/catalog); user can add/remove tiles,
 * switch chart type (pie/doughnut/bar/line/number), and the layout persists.
 */
@Component({
  selector: 'app-ai-store-cockpit',
  templateUrl: './ai-store-cockpit.component.html',
  styleUrls: ['./ai-store-cockpit.component.scss'],
})
export class AiStoreCockpitComponent implements OnInit {
  catalog: any[] = [];
  branches: { id: number; name: string }[] = [];
  selectedBranchId: number | null = null;
  tiles: Tile[] = [];
  addKey = '';
  editMode = true;

  // AI capstones
  briefing = '';
  briefingModel = '';
  briefingLoading = false;
  ask = '';
  askAnswer = '';
  askModel = '';
  askLoading = false;

  readonly chartTypes = ['pie', 'doughnut', 'bar', 'line', 'number'];
  private readonly LS = 'aiStoreCockpit.v1';

  pieColors = [{
    backgroundColor: ['#2e558a', '#5b8fd4', '#1b3465', '#22d3ee', '#10b981',
      '#f59e0b', '#a855f7', '#ef4444', '#94a3b8', '#0d9488', '#6d28d9', '#0891b2'],
  }];
  barColors = [{ backgroundColor: '#2e558a', hoverBackgroundColor: '#1b3465' }];
  lineColors = [{ borderColor: '#2e558a', backgroundColor: 'rgba(46,85,138,.12)', pointBackgroundColor: '#2e558a' }];

  pieOptions: any = { responsive: true, maintainAspectRatio: false, legend: { position: 'right', labels: { boxWidth: 12, fontSize: 11 } } };
  barOptions: any = { responsive: true, maintainAspectRatio: false, legend: { display: false }, scales: { yAxes: [{ ticks: { beginAtZero: true } }], xAxes: [{ ticks: { autoSkip: false, fontSize: 10 }, gridLines: { display: false } }] } };
  lineOptions: any = { responsive: true, maintainAspectRatio: false, legend: { display: false }, elements: { line: { tension: 0.35 } }, scales: { yAxes: [{ ticks: { beginAtZero: true } }] } };

  constructor(private assist: AiAssistService) {}

  ngOnInit(): void {
    this.assist.inventoryBranches().subscribe((r) => { this.branches = (r && r.branches) || []; });
    this.loadBriefing();
    this.assist.storeCatalog().subscribe((r) => {
      this.catalog = (r && r.metrics) || [];
      const saved = this.load();
      // fresh cockpit shows EVERY tile; otherwise restore the saved layout
      const init = saved.length ? saved : this.catalog.map((c) => ({ key: c.key, chart: c.chart }));
      init.forEach((t: any) => this.addTile(t.key, t.chart, false));
    });
  }

  /** Add every catalog tile that isn't already on the board. */
  addAll(): void {
    const have = new Set(this.tiles.map((t) => t.key));
    this.catalog.forEach((c) => { if (!have.has(c.key)) { this.addTile(c.key, null, false); } });
    this.persist();
  }

  addTile(key: string, chart: string | null = null, save = true): void {
    const meta = this.catalog.find((c) => c.key === key);
    const tile: Tile = {
      key, title: meta ? meta.title : key, chart: chart || (meta ? meta.chart : 'bar'),
      loading: true, error: false, labels: [], data: [], value: null, unit: '', real: false, datasets: [],
    };
    this.tiles.push(tile);
    this.loadTile(tile);
    if (save) { this.persist(); }
  }

  loadTile(tile: Tile): void {
    tile.loading = true; tile.error = false;
    this.assist.storeMetric(tile.key, this.selectedBranchId).subscribe({
      next: (r: any) => {
        tile.loading = false;
        if (!r || !r.ok) { tile.error = true; return; }
        tile.title = r.title || tile.title;
        tile.unit = r.unit || '';
        tile.real = !!r.real;
        tile.labels = r.labels || [];
        tile.data = r.data || [];
        tile.value = (r.value !== undefined) ? r.value : null;
        tile.datasets = [{ data: tile.data, label: tile.unit || tile.title }];
      },
      error: () => { tile.loading = false; tile.error = true; },
    });
  }

  setChart(tile: Tile, t: string): void { tile.chart = t; this.persist(); }
  removeTile(i: number): void { this.tiles.splice(i, 1); this.persist(); }
  onAdd(): void { if (this.addKey) { this.addTile(this.addKey); this.addKey = ''; } }
  onBranchChange(): void { this.tiles.forEach((t) => this.loadTile(t)); this.loadBriefing(); }

  loadBriefing(): void {
    this.briefingLoading = true; this.briefing = '';
    this.assist.storeBriefing(this.selectedBranchId).subscribe({
      next: (r: any) => {
        this.briefingLoading = false;
        this.briefing = (r && r.text ? r.text : '').replace(/\*\*/g, '');
        this.briefingModel = (r && r.model) || '';
      },
      error: () => { this.briefingLoading = false; this.briefing = 'Could not reach the AI service.'; },
    });
  }

  onAsk(): void {
    const q = (this.ask || '').trim();
    if (!q) { return; }
    this.askLoading = true; this.askAnswer = '';
    this.assist.storeAsk(q, this.selectedBranchId).subscribe({
      next: (r: any) => {
        this.askLoading = false;
        this.askAnswer = (r && r.answer) || 'No answer.';
        this.askModel = (r && r.model) || '';
        const ck = r && r.chart;
        if (ck && !this.tiles.find((t) => t.key === ck)) { this.addTile(ck); }
      },
      error: () => { this.askLoading = false; this.askAnswer = 'Could not reach the AI service.'; },
    });
  }
  refresh(): void { this.tiles.forEach((t) => this.loadTile(t)); }
  resetLayout(): void { this.tiles = []; localStorage.removeItem(this.LS); this.addAll(); }

  isPie(t: Tile): boolean { return t.chart === 'pie' || t.chart === 'doughnut'; }
  isNumber(t: Tile): boolean { return t.chart === 'number'; }
  fmt(v: number): string { return '₹' + (v || 0).toLocaleString('en-IN'); }
  catName(key: string): string { const m = this.catalog.find((c) => c.key === key); return m ? m.title : key; }

  private persist(): void {
    try { localStorage.setItem(this.LS, JSON.stringify(this.tiles.map((t) => ({ key: t.key, chart: t.chart })))); } catch { /* ignore */ }
  }
  private load(): any[] {
    try { return JSON.parse(localStorage.getItem(this.LS) || '[]'); } catch { return []; }
  }
}
